import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit3, FiSave, FiCheck } = FiIcons;

const LessonNotes = ({ lessonId, notes, onSave, loading }) => {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const saveTimeoutRef = useRef(null);
  const textareaRef = useRef(null);

  // Load existing notes
  useEffect(() => {
    const existingNote = notes.find(note => note.lesson_id === lessonId);
    if (existingNote) {
      setContent(existingNote.content || '');
    } else {
      setContent('');
    }
    setHasChanges(false);
  }, [lessonId, notes]);

  // Auto-save functionality
  useEffect(() => {
    if (hasChanges && content.trim()) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for auto-save
      saveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 5000); // Auto-save after 5 seconds of inactivity
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [content, hasChanges]);

  const handleContentChange = (e) => {
    setContent(e.target.value);
    setHasChanges(true);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;

    setSaving(true);
    try {
      await onSave(lessonId, content);
      setHasChanges(false);
      setSaved(true);
      
      // Hide saved indicator after 2 seconds
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBlur = () => {
    if (hasChanges) {
      handleSave();
    }
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiEdit3} className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">My Notes</h3>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Save Status */}
              {saved && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center space-x-1 text-green-600 text-sm"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span>Saved</span>
                </motion.div>
              )}
              
              {saving && (
                <div className="flex items-center space-x-1 text-gray-500 text-sm">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  <span>Saving...</span>
                </div>
              )}

              {hasChanges && !saving && (
                <span className="text-xs text-amber-600">Unsaved changes</span>
              )}

              {/* Manual Save Button */}
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-nutrition-600 text-white rounded-md hover:bg-nutrition-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>

        {/* Notes Content */}
        <div className="p-6">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleContentChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="Take notes about this lesson... (Ctrl+S to save manually)"
            className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500 font-mono text-sm"
            disabled={loading}
          />

          {/* Tips */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Notes Tips:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Notes are automatically saved every 5 seconds</li>
              <li>• Press Ctrl+S to save manually</li>
              <li>• Notes are saved when you click outside the text area</li>
              <li>• Your notes are private and only visible to you</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LessonNotes;